import React from "react";
import { useLang } from "../../hooks/useLang";
import { FaCode, FaPaintBrush, FaTools } from "react-icons/fa";
import SEO from "../../components/ui/SEO";
import proyectos from "../../data/proyectosData";
import { profile } from "../../data/profile";
import CTASection from "../../components/sections/CTASection";
import FlipCard from "../../components/ui/FlipCard";
import Button from "../../components/ui/Button";
import "./Home.css";

const getProjectImageSrcSet = (imagePath) => {
  return `${imagePath.replace(/\.webp$/, "-small.webp")} 480w, ${imagePath} 800w`;
};

function Inicio() {
  const { t, lang } = useLang();

  const services = [
    {
      icono: FaCode,
      titulo: t("home.services.web_title"),
      descripcion: t("home.services.web_desc"),
    },
    {
      icono: FaPaintBrush,
      titulo: t("home.services.design_title"),
      descripcion: t("home.services.design_desc"),
    },
    {
      icono: FaTools,
      titulo: t("home.services.support_title"),
      descripcion: t("home.services.support_desc"),
    },
  ];

  return (
    <>
      <SEO
        title={t("home.meta.title")}
        description={t("home.meta.description")}
      />

      <div className="inicio-container">
        {/* === BANNER PRINCIPAL === */}
        <CTASection
          id="banner"
          title={t("home.banner.title")}
          subtitle={
            <span className="subtitle-typing">{t("home.banner.subtitle")}</span>
          }
          backgroundImage="/images/banner-480.webp"
          buttons={[
            {
              text: t("home.banner.cta_services"),
              link: "/servicios",
              type: "primary",
              ariaLabel: t("home.banner.cta_services"),
            },
            {
              text: t("home.banner.cta_portfolio"),
              link: "/portafolio",
              type: "secondary",
              ariaLabel: t("home.banner.cta_portfolio"),
            },
          ]}
          align="center"
          className="banner-principal-section hero-section"
          isHero={true}
          imageSrcSet="/images/banner-360.webp 360w, /images/banner-480.webp 480w, /images/banner-768.webp 768w, /images/banner-1280.webp 1280w, /images/banner-1600.webp 1600w, /images/banner-1920.webp 1920w"
          imageSizes="100vw"
        />

        {/* === SECCIÓN PRESENTACIÓN === */}
        <CTASection
          id="sobre-mi"
          title={t("home.about.title")}
          subtitle={t("home.about.text")}
          backgroundImage="/images/ctainicio1.webp"
          overlayColor="rgba(13, 60, 116, 0.65)"
          buttons={[
            {
              text: t("home.about.cv_es"),
              link: profile.urls.cvSpanish,
              type: "primary",
              isExternal: true,
              ariaLabel: t("home.about.cv_es"),
            },
            {
              text: t("home.about.cv_en"),
              link: profile.urls.cvEnglish,
              type: "secondary",
              isExternal: true,
              ariaLabel: t("home.about.cv_en"),
            },
          ]}
          align="center"
          className="presentacion-section"
        />

        {/* === SECCIÓN SERVICIOS === */}
        <section className="servicios" aria-labelledby="servicios-heading">
          <h2 id="servicios-heading">{t("home.services.title")}</h2>
          <div className="card-grid">
            {services.map((servicio, index) => (
              <FlipCard
                key={servicio.titulo}
                index={index}
                role="article"
                ariaLabel={servicio.titulo}
                frontContent={
                  <>
                    <servicio.icono className="icono" />
                    <h3 id={`servicio-${index}`}>{servicio.titulo}</h3>
                  </>
                }
                backContent={
                  <>
                    <p>{servicio.descripcion}</p>
                    <Button
                      to="/servicios"
                      variant="card"
                      ariaLabel={`${t("home.services.view_details")} ${servicio.titulo}`}
                    >
                      {t("home.services.view_details")}
                    </Button>
                  </>
                }
              />
            ))}
          </div>
        </section>

        {/* === SECCIÓN PROYECTOS RECIENTES (DINÁMICA) === */}
        <section
          className="proyectos-recientes"
          aria-labelledby="proyectos-heading"
        >
          <h2 id="proyectos-heading">{t("home.projects.title")}</h2>
          <div className="card-grid">
            {proyectos.slice(0, 3).map((proyecto, index) => (
              <FlipCard
                key={proyecto.id || index}
                index={index}
                role="article"
                ariaLabel={proyecto.titulo[(lang || "es").split("-")[0]]}
                frontContent={
                  <>
                    <img
                      src={proyecto.imagen}
                      srcSet={getProjectImageSrcSet(proyecto.imagen)}
                      sizes="(max-width: 600px) 480px, 800px"
                      alt={`Vista previa del proyecto ${proyecto.titulo[(lang || "es").split("-")[0]]}`}
                      className="proyecto-imagen"
                      width={proyecto.width}
                      height={proyecto.height}
                      loading="lazy"
                    />
                    <h3 id={`proyecto-${index}`}>
                      {proyecto.titulo[(lang || "es").split("-")[0]]}
                    </h3>
                  </>
                }
                backContent={
                  <>
                    <p>{proyecto.descripcion[(lang || "es").split("-")[0]]}</p>
                    <Button
                      href={proyecto.url}
                      variant="card"
                      ariaLabel={`${t("home.projects.view_project")} ${proyecto.titulo[(lang || "es").split("-")[0]]}`}
                    >
                      {t("home.projects.view_project")}
                    </Button>
                  </>
                }
              />
            ))}
          </div>

          <div className="ver-mas-cta">
            <Button
              to="/portafolio"
              variant="primary"
              ariaLabel={t("home.projects.view_all")}
            >
              {t("home.projects.view_all")}
            </Button>
          </div>
        </section>

        {/* === CTA FINAL === */}
        <CTASection
          id="cta-final"
          title={t("home.cta_final.title")}
          backgroundImage="/images/ctainicio2.webp"
          overlayColor="rgba(13, 60, 116, 0.40)"
          buttons={[
            {
              text: t("home.cta_final.contact"),
              link: "/contacto",
              type: "primary",
              ariaLabel: t("home.cta_final.contact"),
            },
            {
              text: t("home.cta_final.services"),
              link: "/servicios",
              type: "secondary",
              ariaLabel: t("home.cta_final.services"),
            },
          ]}
          align="center"
          className="cta-final-section"
        />
      </div>
    </>
  );
}

export default Inicio;
